/**
 * The fuzzy-date group widget — docs/08-frontend-and-ux.md §3.5, ADR-0004.
 *
 * Year / month / day with the dependency chain *enforced by the control itself*
 * (day disabled until a month, month until a year), a qualifier, the verbatim
 * "as written" text, and a live preview using the display rules of docs/02 §4.
 *
 * "Unknown" is the default state and is reached by clearing the year — never a
 * checkbox that hides the fields, which would make an unknown date look like an
 * unasked question.
 */

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DATE_QUALIFIER,
  FUZZY_YEAR_MAX,
  FUZZY_YEAR_MIN,
  daysInMonth,
  formatFuzzyDate,
  validateFuzzyDate,
  type DateQualifier,
  type FuzzyDateParts,
} from '@famtree/shared';
import styles from './components.module.css';
import fieldStyles from './FuzzyDateField.module.css';
import { RelativeDateField } from './RelativeDateField.js';

export interface FuzzyDateFieldProps {
  legend: string;
  value: FuzzyDateParts | null;
  onChange: (value: FuzzyDateParts | null) => void;
  disabled?: boolean;
  /**
   * Offer "or say when, relative to something else" (ADR-0008). On by default;
   * off where there is nothing sensible to anchor to, such as a photo's date
   * inside the bulk uploader.
   */
  allowRelative?: boolean;
}

/** `null` for "cleared", a number for a parsed value; NaN never escapes. */
function toNumber(raw: string): number | null {
  if (raw.trim() === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function FuzzyDateField({
  legend,
  value,
  onChange,
  disabled = false,
  allowRelative = true,
}: FuzzyDateFieldProps): JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const [relativeMode, setRelativeMode] = useState(false);
  const date = value ?? {};
  const preview = formatFuzzyDate(date);
  const problems = validateFuzzyDate(date);

  /** Every edit goes through here, so the chain can never be broken by a click. */
  const update = (patch: Partial<FuzzyDateParts>): void => {
    const next: FuzzyDateParts = { ...date, ...patch };
    // Clearing a level clears everything below it: a month without a year is a
    // CHECK violation, and silently keeping it would fail only at submit.
    if (next.year == null) {
      next.month = null;
      next.day = null;
      next.qualifier = null;
    }
    if (next.month == null) next.day = null;
    if (next.year != null && next.qualifier == null) next.qualifier = 'exact';

    const empty =
      next.year == null &&
      next.month == null &&
      next.day == null &&
      (next.text == null || next.text.trim() === '');
    onChange(empty ? null : next);
  };

  const maxDay = date.year != null && date.month != null ? daysInMonth(date.year, date.month) : 31;

  return (
    <fieldset className={fieldStyles.group} disabled={disabled}>
      <legend className={styles.label}>{legend}</legend>

      <div className={fieldStyles.row}>
        <label className={fieldStyles.part}>
          <span className={styles.muted}>{t('fuzzyDate.year')}</span>
          <input
            className={styles.input}
            id={`${id}-year`}
            type="number"
            inputMode="numeric"
            min={FUZZY_YEAR_MIN}
            max={FUZZY_YEAR_MAX}
            value={date.year ?? ''}
            onChange={(event) => update({ year: toNumber(event.target.value) })}
          />
        </label>

        <label className={fieldStyles.part}>
          <span className={styles.muted}>{t('fuzzyDate.month')}</span>
          <select
            className={styles.input}
            id={`${id}-month`}
            value={date.month ?? ''}
            disabled={date.year == null}
            onChange={(event) => update({ month: toNumber(event.target.value) })}
          >
            <option value="">{t('fuzzyDate.none')}</option>
            {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
              <option key={month} value={month}>
                {String(month).padStart(2, '0')}
              </option>
            ))}
          </select>
        </label>

        <label className={fieldStyles.part}>
          <span className={styles.muted}>{t('fuzzyDate.day')}</span>
          <select
            className={styles.input}
            id={`${id}-day`}
            value={date.day ?? ''}
            disabled={date.month == null}
            onChange={(event) => update({ day: toNumber(event.target.value) })}
          >
            <option value="">{t('fuzzyDate.none')}</option>
            {Array.from({ length: maxDay }, (_, index) => index + 1).map((day) => (
              <option key={day} value={day}>
                {String(day).padStart(2, '0')}
              </option>
            ))}
          </select>
        </label>

        <label className={fieldStyles.part}>
          <span className={styles.muted}>{t('fuzzyDate.qualifier')}</span>
          <select
            className={styles.input}
            id={`${id}-qualifier`}
            value={date.qualifier ?? 'exact'}
            disabled={date.year == null}
            onChange={(event) => update({ qualifier: event.target.value as DateQualifier })}
          >
            {DATE_QUALIFIER.map((qualifier) => (
              <option key={qualifier} value={qualifier}>
                {t(`fuzzyDate.qualifiers.${qualifier}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className={fieldStyles.asWritten}>
        <span className={styles.muted}>{t('fuzzyDate.asWritten')}</span>
        <input
          className={styles.input}
          id={`${id}-text`}
          type="text"
          maxLength={200}
          placeholder={t('fuzzyDate.asWrittenPlaceholder')}
          value={date.text ?? ''}
          onChange={(event) => update({ text: event.target.value })}
        />
      </label>

      <p className={styles.muted} aria-live="polite">
        {t('fuzzyDate.preview')}: <strong>{preview ?? t('fuzzyDate.unknown')}</strong>
      </p>

      {problems.length > 0 && (
        <p className={styles.fieldError}>
          {problems.map((problem) => t(`fuzzyDate.problems.${problem}`)).join(' ')}
        </p>
      )}
      {allowRelative && !disabled && (
        <div>
          <button
            type="button"
            className={styles.buttonSecondary}
            onClick={() => setRelativeMode((mode) => !mode)}
          >
            {t(relativeMode ? 'relativeDate.hide' : 'relativeDate.toggle')}
          </button>
          {relativeMode && (
            <RelativeDateField
              legend={legend}
              onResolved={(resolved) => {
                onChange(resolved);
                setRelativeMode(false);
              }}
            />
          )}
        </div>
      )}
    </fieldset>
  );
}

/** Read-only rendering of a stored date, used by profiles and list rows. */
export function FuzzyDateText({ value }: { value: FuzzyDateParts | null }): JSX.Element {
  const { t } = useTranslation();
  const rendered = formatFuzzyDate(value);
  return rendered ? (
    <span className="tabular">{rendered}</span>
  ) : (
    <span className={styles.muted}>{t('fuzzyDate.unknown')}</span>
  );
}
